import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/api/client";
import { ep } from "@/api/endpoints";
import { qk } from "@/api/queryKeys";
import type { ConversationList } from "@/api/types";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";

export type LeaveTarget = { conversationId: string; plotId: string };

/**
 * DELETE /api/conversations/:id (대화 목록 설계 D13·D15). 성공하면 목록에서 그 행을 빼고 초안을 지운 뒤 스낵바.
 * 채팅방 캐시(qk.conversation)는 지우지 않는다 — 채팅방이 떠 있는 채로 지우면 get-or-create 재조회로 새 대화가 생긴다.
 * 채팅방에서 나갈 때는 화면이 내려간 뒤 `useForgetConversation` 으로 지운다.
 */
export function useLeaveConversation() {
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? "");
  return useMutation({
    mutationFn: ({ conversationId }: LeaveTarget) => apiFetch<void>(ep.conversation(conversationId), { method: "DELETE" }),
    onSuccess: (_data, { conversationId, plotId }) => {
      qc.setQueryData<ConversationList>(qk.conversationList(userId), (old) => (old ? { items: old.items.filter((i) => i.conversationId !== conversationId) } : old));
      useUiStore.getState().setDraft(plotId, "");
      useUiStore.getState().showSnackbar(t("leave.done"));
    },
  });
}

/** 나간 대화의 채팅방 캐시를 지운다(다시 들어가면 새 대화를 받도록). 그 채팅방 화면이 마운트돼 있지 않을 때 부른다 */
export function useForgetConversation() {
  const qc = useQueryClient();
  return (plotId: string) => qc.removeQueries({ queryKey: qk.conversation(plotId) });
}
